import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AgGridModule } from 'ag-grid-angular';
import { AppRoutingModule } from './app-routing.module';
import { HttpClientModule, provideHttpClient, withInterceptors } from '@angular/common/http';
import { ModuleRegistry } from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import { AppComponent } from './app.component';
import { NgbDateAdapter, NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { DragDropModule } from '@angular/cdk/drag-drop';

import { FormsModule } from '@angular/forms';
import { LoginComponent } from './no-auth/login/login.component';
import { LayoutComponent } from './auth/layout/layout.component';
import { DashboardComponent } from './auth/dashboard/dashboard.component';
import { CountryComponent } from './auth/masters/country/country.component';
import { CurrencyComponent } from './auth/masters/currency/currency.component';
import { CountryCurrencyComponent } from './auth/masters/country-currency/country-currency.component';
import { ProviderComponent } from './auth/masters/provider/provider.component';
import { ConversionComponent } from './auth/conversion/conversion.component';
import { SettingsComponent } from './auth/settings/settings.component';
import { CustomersComponent } from './auth/customers/customers.component';
import { TransactionsComponent } from './auth/transactions/transactions.component';
import { UsersComponent } from './auth/users/users.component';
import { NewUserComponent } from './auth/users/new-user/new-user.component';
import { OnlyNumberDirective } from './shared/directives/onlyNumbers.directives';
import { DisputesComponent } from './auth/disputes/disputes.component';
import { jwtInterceptor } from './shared/helpers/jwt.interceptor';
import { errorInterceptor } from './shared/helpers/error.interceptor';
import { ViewDisputeComponent } from './auth/disputes/view-dispute/view-dispute.component';
import { ReportsComponent } from './auth/reports/reports.component';
import { CustomAdapter, CustomDateParserFormatter } from './shared/pipes/date.formatter';
import { ForgotPasswordComponent } from './no-auth/forgot-password/forgot-password.component';
import { RbacComponent } from './auth/rbac/rbac.component';
import { RefundsComponent } from './auth/refunds/refunds.component';
import { ViewTransactionComponent } from './auth/transactions/view-transaction/view-transaction.component';
import { ViewImageComponent } from './shared/components/view-image/view-image.component';
import { PartnersComponent } from './auth/partners/partners.component';
import { AddPartnerComponent } from './auth/partners/add-partner/add-partner.component';
import { LedgersComponent } from './auth/ledgers/ledgers.component';
import { DateFilterComponent } from './shared/components/date-filter/date-filter.component';
import { SettlementsComponent } from './auth/settlements/settlements.component';
import { RiskComponent } from './auth/dashboard/risk/risk.component';
import { OpsComponent } from './auth/dashboard/ops/ops.component';
import { MfaComponent } from './auth/settings/mfa/mfa.component';
import { MfaLoginComponent } from './no-auth/mfa-login/mfa-login.component';
import { FirstMfaSetupComponent } from './no-auth/first-mfa-setup/first-mfa-setup.component';
import { MfaPromptComponent } from './auth/dashboard/mfa-prompt/mfa-prompt.component';
import { CustomerLimitsComponent } from './auth/customer-limits/customer-limits.component';
import { PartnerLimitsComponent } from './auth/partner-limits/partner-limits.component';
import { PackagesComponent } from './auth/airalo/packages/packages.component';
import { OrdersComponent } from './auth/airalo/orders/orders.component';
import { OrderDetailsComponent } from './auth/airalo/orders/order-details/order-details.component';
import { SimsComponent } from './auth/airalo/sims/sims.component';
import { SimDetailsComponent } from './auth/airalo/sims/sim-details/sim-details.component';
import { SimUsageComponent } from './auth/airalo/sims/sim-usage/sim-usage.component';
import { SimPackageHistoryComponent } from './auth/airalo/sims/sim-package-history/sim-package-history.component';
import { SimInstructionsComponent } from './auth/airalo/sims/sim-instructions/sim-instructions.component';
import { SimCompatibleDevicesComponent } from './auth/airalo/sims/sim-compatible-devices/sim-compatible-devices.component';
import { BalanceComponent } from './auth/airalo/balance/balance.component';
import { CustomerDetailsComponent } from './auth/customers/customer-details/customer-details.component';
import { BeneficiaryTransactionsComponent } from './auth/customers/beneficiary-transactions/beneficiary-transactions.component';
import { CountryDynamicFieldsComponent } from './auth/masters/country/dynamicfields/dynamicfields.component';
import { CountryRemittanceConfigComponent } from './auth/masters/country/remittance-config/remittance-config.component';
import { BillsComponent } from './auth/bills/bills.component';
import { ResetPasswordComponent } from './auth/reset-password/reset-password.component';
import { SecurityQuestionsComponent } from './auth/settings/security-questions/security-questions.component';
import { SafeHtmlPipe } from './shared/pipes/safe-html.pipe';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    LayoutComponent,
    DashboardComponent,
    CountryComponent,
    CurrencyComponent,
    CountryCurrencyComponent,
    ConversionComponent,
    SettingsComponent,
    CustomersComponent,
    TransactionsComponent,
    UsersComponent,
    NewUserComponent,
    OnlyNumberDirective,
    DisputesComponent,
    ViewDisputeComponent,
    ReportsComponent,
    ForgotPasswordComponent,
    RbacComponent,
    RefundsComponent,
    ViewTransactionComponent,
    ViewImageComponent,
    PartnersComponent,
    AddPartnerComponent,
    LedgersComponent,
    DateFilterComponent,
    SettlementsComponent,
    RiskComponent,
    OpsComponent,
    MfaComponent,
    MfaLoginComponent,
    FirstMfaSetupComponent,
    MfaPromptComponent,
    PackagesComponent,
    OrdersComponent,
    OrderDetailsComponent,
    SimsComponent,
    SimDetailsComponent,
    SimUsageComponent,
    SimPackageHistoryComponent,
    SimInstructionsComponent,
    SimCompatibleDevicesComponent,
    BalanceComponent,
    CustomerLimitsComponent,
    PartnerLimitsComponent,
    ProviderComponent,
    CustomerDetailsComponent,

    BeneficiaryTransactionsComponent,
    BillsComponent,
    CountryDynamicFieldsComponent,
    CountryRemittanceConfigComponent,
    ResetPasswordComponent,
    SecurityQuestionsComponent,
  ],
  imports: [
    SafeHtmlPipe,
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    NgbModule,
    AgGridModule,
    FormsModule,
    HttpClientModule,
    NgxChartsModule,
    DragDropModule
  ],
  providers: [
    provideHttpClient(withInterceptors([jwtInterceptor, errorInterceptor])),
    { provide: NgbDateAdapter, useClass: CustomAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor() {
    // Register the AG Grid modules globally
    ModuleRegistry.registerModules([AllCommunityModule]); // Register all community modules
  }
}